/**
 * Utilidades para procesamiento de imágenes en el cliente
 * - Redimensionamiento antes de subir
 * - Compresión para reducir tamaño
 * - Conversión a formatos optimizados
 */

/**
 * Redimensiona una imagen manteniendo el aspect ratio
 * @param {File} file - Archivo de imagen original
 * @param {number} maxWidth - Ancho máximo
 * @param {number} maxHeight - Alto máximo
 * @param {number} quality - Calidad de compresión (0-1)
 * @returns {Promise<File>} - Archivo redimensionado
 */
export const resizeImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Crear canvas para redimensionar
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir canvas a blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al procesar la imagen'));
              return;
            }

            // Crear nuevo File con el blob redimensionado
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });

            console.log('[ImageUtils] Imagen redimensionada:', {
              original: `${file.size} bytes`,
              resized: `${resizedFile.size} bytes`,
              reduction: `${((1 - resizedFile.size / file.size) * 100).toFixed(1)}%`,
              dimensions: `${width}x${height}`,
            });

            resolve(resizedFile);
          },
          file.type,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Redimensiona imagen para avatar (circular, pequeño)
 * @param {File} file - Archivo de imagen original
 * @returns {Promise<File>} - Archivo redimensionado para avatar
 */
export const resizeAvatar = (file) => {
  // Avatares: 300x300px, mayor compresión
  return resizeImage(file, 300, 300, 0.8);
};

/**
 * Redimensiona imagen para items de menú
 * @param {File} file - Archivo de imagen original
 * @returns {Promise<File>} - Archivo redimensionado para menú
 */
export const resizeMenuItem = (file) => {
  // Items de menú: 800x800px
  return resizeImage(file, 800, 800, 0.85);
};

/**
 * Redimensiona imagen para logos de restaurante
 * @param {File} file - Archivo de imagen original
 * @returns {Promise<File>} - Archivo redimensionado para logo
 */
export const resizeRestaurantLogo = (file) => {
  // Logos: 500x500px
  return resizeImage(file, 500, 500, 0.9);
};

/**
 * Valida que el archivo sea una imagen válida
 * @param {File} file - Archivo a validar
 * @param {number} maxSizeMB - Tamaño máximo en MB
 * @returns {Object} - { valid: boolean, error: string | null }
 */
export const validateImageFile = (file, maxSizeMB = 5) => {
  if (!file) {
    return { valid: false, error: 'No se seleccionó ningún archivo' };
  }

  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'El archivo debe ser una imagen' };
  }

  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return { valid: false, error: `La imagen debe ser menor a ${maxSizeMB}MB` };
  }

  return { valid: true, error: null };
};

/**
 * Genera un preview URL de una imagen
 * @param {File} file - Archivo de imagen
 * @returns {Promise<string>} - Data URL para preview
 */
export const generatePreview = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
